import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Search, Trash2, X } from 'lucide-react';

/**
 * OfferManagerDialog
 *  mode='by-user'     : manage offers for a specific user (pick products + price each)
 *  mode='by-product'  : manage offers for a specific product (pick users + common/individual price)
 */
export function OfferManagerDialog({ open, onOpenChange, mode, target }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const productsQ = useQuery({
    queryKey: ['admin', 'products'],
    queryFn: async () => (await api.get('/admin/products')).data,
    enabled: open,
  });
  const usersQ = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async () => (await api.get('/admin/users')).data,
    enabled: open,
  });
  const offersQ = useQuery({
    queryKey: ['admin', 'offers', mode, target?.id ?? target?._odoo_id],
    queryFn: async () => {
      const params = mode === 'by-user' ? { user_id: target.id } : { product_id: target._odoo_id };
      return (await api.get('/admin/offers', { params })).data;
    },
    enabled: open && !!target,
  });

  const [q, setQ] = useState('');
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notes, setNotes] = useState('');
  const [commonPrice, setCommonPrice] = useState('');
  const [rows, setRows] = useState({}); // key -> {price:'', active:true}

  React.useEffect(() => {
    if (!open) return;
    // pre-fill from existing offers only; rows without existing offer stay
    // "inactive" and price empty (user must explicitly opt in via checkbox).
    const map = {};
    (offersQ.data?.items || []).forEach((o) => {
      const key = mode === 'by-user' ? String(o.product_id) : o.user_id;
      map[key] = { price: String(o.price), active: !!o.active, offerId: o.id, notes: o.notes || '' };
    });
    setRows(map);
  }, [open, offersQ.data, mode]);

  const products = productsQ.data?.items || [];
  const users = (usersQ.data?.items || []).filter((u) => u.role !== 'admin');

  const list = useMemo(() => {
    if (mode === 'by-user') {
      const ql = q.toLowerCase().trim();
      return products.filter((p) => !ql || (p.name || '').toLowerCase().includes(ql) || (p.default_code || '').toLowerCase().includes(ql));
    }
    const ql = q.toLowerCase().trim();
    return users.filter((u) => !ql || (u.email || '').toLowerCase().includes(ql) || (u.full_name || '').toLowerCase().includes(ql));
  }, [mode, products, users, q]);

  const singleMut = useMutation({
    mutationFn: async ({ userId, productId, price, active, offerNotes }) =>
      (await api.post(`/admin/users/${userId}/offers`, {
        product_id: productId,
        price: parseFloat(price),
        currency: 'EUR', unit: 'kg',
        notify: notifyEmail,
        notes: offerNotes || notes || null,
        active,
      })).data,
  });

  const bulkMut = useMutation({
    mutationFn: async (payload) => (await api.post(`/admin/products/${target._odoo_id}/offers`, payload)).data,
  });

  const deleteMut = useMutation({
    mutationFn: async (offerId) => (await api.delete(`/admin/offers/${offerId}`)).data,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'offers'] });
    qc.invalidateQueries({ queryKey: ['products'] });
  };

  const saveRow = async (key, row) => {
    if (!row.price || parseFloat(row.price) <= 0) return toast.error(t('offers.invalid_price'));
    try {
      if (mode === 'by-user') {
        await singleMut.mutateAsync({ userId: target.id, productId: parseInt(key, 10), price: row.price, active: row.active !== false, offerNotes: row.notes });
      } else {
        // by-product: one user at a time
        await singleMut.mutateAsync({ userId: key, productId: target._odoo_id, price: row.price, active: row.active !== false, offerNotes: row.notes });
      }
      toast.success(t('offers.saved'));
      invalidate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('offers.error'));
    }
  };

  const applyCommonToAll = () => {
    if (!commonPrice) return;
    const next = { ...rows };
    list.forEach((entry) => {
      const key = mode === 'by-user' ? String(entry._odoo_id) : entry.id;
      const prev = next[key] || {};
      next[key] = { ...prev, price: String(commonPrice), active: true };
    });
    setRows(next);
  };

  const setAllActive = (value) => {
    const next = { ...rows };
    list.forEach((entry) => {
      const key = mode === 'by-user' ? String(entry._odoo_id) : entry.id;
      const prev = next[key] || { price: '' };
      next[key] = { ...prev, active: value };
    });
    setRows(next);
  };

  const bulkSave = async () => {
    if (mode !== 'by-product') return;
    // Only rows explicitly marked active AND with a valid price are saved.
    const assignments = Object.entries(rows)
      .filter(([, v]) => v?.active === true && v?.price && parseFloat(v.price) > 0)
      .map(([user_id, v]) => ({ user_id, price: parseFloat(v.price) }));
    if (assignments.length === 0) return toast.error(t('offers.no_selection'));
    try {
      const res = await bulkMut.mutateAsync({
        common_price: commonPrice ? parseFloat(commonPrice) : null,
        currency: 'EUR', unit: 'kg', notes: notes || null, notify: notifyEmail, active: true,
        assignments,
      });
      const ok = (res.results || []).filter((r) => r.status === 'ok').length;
      toast.success(t('offers.bulk_saved', { ok, total: assignments.length }));
      invalidate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('offers.error'));
    }
  };

  const removeOffer = async (offerId) => {
    if (!offerId) return;
    try { await deleteMut.mutateAsync(offerId); toast.success('OK'); invalidate(); }
    catch { toast.error(t('offers.error')); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {mode === 'by-user'
              ? t('offers.title_by_user', { name: target?.full_name || target?.email })
              : t('offers.title_by_product', { name: target?.name })}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#4A4D52]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={mode === 'by-user' ? t('offers.search_products') : t('offers.search_users')} className="h-10 w-full rounded-lg border border-[#E4E9F0] bg-white pl-9 pr-3 text-sm outline-none focus:border-[#E2611C]" data-testid="offers-search" />
            </div>
            {mode === 'by-product' && (
              <div className="flex items-center gap-2">
                <input
                  value={commonPrice} onChange={(e) => setCommonPrice(e.target.value)}
                  placeholder={t('offers.common_price')} type="number" step="0.01" min="0"
                  className="h-10 w-40 rounded-lg border border-[#E4E9F0] bg-white px-3 text-sm font-mono outline-none focus:border-[#E2611C]" data-testid="offers-common-price"
                />
                <button onClick={applyCommonToAll} className="pn-btn-outline !py-2 !px-3" data-testid="offers-apply-common">{t('offers.apply_to_all')}</button>
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-2">
            <button onClick={() => setAllActive(true)} data-testid="offers-select-all" className="rounded-full border border-[#E4E9F0] bg-white px-3 py-1 text-xs font-semibold hover:border-[#E2611C]/40">
              {t('offers.select_all')}
            </button>
            <button onClick={() => setAllActive(false)} data-testid="offers-clear-all" className="rounded-full border border-[#E4E9F0] bg-white px-3 py-1 text-xs font-semibold hover:border-[#B42318]/40">
              {t('offers.clear_all')}
            </button>
          </div>

          <div className="max-h-[380px] overflow-auto rounded-lg border border-[#E4E9F0]">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#F6F8FB] text-xs uppercase tracking-wide text-[#4A4D52]">
                <tr>
                  <th className="px-3 py-2">{mode === 'by-user' ? t('offers.product') : t('offers.customer')}</th>
                  <th className="px-3 py-2 w-40">{t('offers.price_eur_kg')}</th>
                  <th className="px-3 py-2 w-24">{t('offers.status')}</th>
                  <th className="px-3 py-2 w-32 text-right">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {list.map((entry) => {
                  const key = mode === 'by-user' ? String(entry._odoo_id) : entry.id;
                  const row = rows[key] || { price: '', active: false };
                  const active = row.active === true;
                  return (
                    <tr key={key} className="border-t border-[#E4E9F0]">
                      <td className="px-3 py-2">
                        {mode === 'by-user' ? (
                          <div>
                            <div className="font-medium">{entry.name}</div>
                            <div className="font-mono text-[11px] text-[#4A4D52]">{entry.default_code || '—'} · Odoo #{entry._odoo_id}</div>
                          </div>
                        ) : (
                          <div>
                            <div className="font-medium">{entry.full_name || '—'}</div>
                            <div className="font-mono text-[11px] text-[#4A4D52]">{entry.email}</div>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number" step="0.01" min="0"
                          value={row.price}
                          onChange={(e) => setRows({ ...rows, [key]: { ...row, price: e.target.value } })}
                          className="h-9 w-32 rounded-md border border-[#E4E9F0] bg-white px-2 font-mono text-sm outline-none focus:border-[#E2611C]"
                          data-testid={`offer-price-${key}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <label className="inline-flex items-center gap-1.5 text-xs">
                          <input type="checkbox" checked={active} onChange={(e) => setRows({ ...rows, [key]: { ...row, active: e.target.checked } })} />
                          {active ? t('offers.active') : t('offers.inactive')}
                        </label>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex gap-2">
                          <button onClick={() => saveRow(key, row)} data-testid={`offer-save-${key}`} className="rounded-full bg-[#E2611C] px-3 py-1 text-xs font-semibold text-white hover:brightness-95">{t('common.save')}</button>
                          {row.offerId && (
                            <button onClick={() => removeOffer(row.offerId)} data-testid={`offer-del-${key}`} className="rounded-full border border-[#E4E9F0] bg-white px-2 py-1 text-xs text-[#B42318] hover:border-[#B42318]/40"><Trash2 className="h-3.5 w-3.5" /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {list.length === 0 && (
                  <tr><td colSpan={4} className="p-6 text-center text-[#4A4D52]">{t('catalog.empty')}</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('offers.notes_placeholder')} className="w-full rounded-lg border border-[#E4E9F0] bg-white p-2 text-sm outline-none focus:border-[#E2611C]" data-testid="offers-notes" />
          <label className="inline-flex items-center gap-2 text-xs text-[#4A4D52]">
            <input type="checkbox" checked={notifyEmail} onChange={(e) => setNotifyEmail(e.target.checked)} data-testid="offers-notify" /> {t('offers.notify_email')}
          </label>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <div className="text-[11px] text-[#4A4D52]">{t('offers.hint')}</div>
          <div className="flex gap-2">
            <button onClick={() => onOpenChange(false)} className="pn-btn-outline !py-2 !px-4">{t('common.close')}</button>
            {mode === 'by-product' && (
              <button onClick={bulkSave} className="pn-btn-orange !py-2 !px-4" data-testid="offers-bulk-save">{t('offers.bulk_save')}</button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
